import * as THREE from 'three';
import { CARD_WIDTH, CARD_HEIGHT, CARD_THICKNESS } from '../cardMesh';

// ── Lethal. Skull-shaped gap in drifting fog ────────────────────
//
// A bone-shaped void cut into a layer of slow-drifting black fog.
// The skull is NEGATIVE SPACE. The fog can't enter the skull bone
// region, so as the noise pattern drifts across the card on the
// wind, the skull silhouette is revealed against the card art.
//
// Eye sockets and the nasal cavity are SUBTRACTED from the skull
// bone SDF. Meaning those points lie OUTSIDE the bone, so fog
// re-enters there. The result is the iconic skull-face read: clear
// cranium+jaw silhouette with three darker fog-filled features
// (two eye sockets + nose) inside it. The card's existing art
// provides the bone "color" for free.
//
// Wind blows from 60° clockwise off vertical (upper-right). Noise
// UVs scroll opposite the flow direction so the visible fog
// features appear to drift WITH the wind. Stationary skull,
// moving fog = "wind drifting around an obstacle" without any
// actual fluid simulation.
//
// Architecture: standard lay-flat + stack-occlusion-safe pattern
// shared by stealth / ward / airborne.

const LAYER_COUNT = 4;
const RISE_HEIGHT = 0.2;
// Plane size. Needs to fully contain the Gaussian fog patch with
// enough tail room for the falloff to reach near-zero before the
// plane boundary. With a centered patch sized to fit within the
// card, ~1.4× card dims gives the Gaussian ~3 units of tail past
// the visible patch radius.
const PLANE_W = CARD_WIDTH * 1.4;
const PLANE_H = CARD_HEIGHT * 1.3;

// Skull size in world units. ~3.0 puts the silhouette roughly at
// half the card height. Big enough to be unmistakable, small
// enough that the surrounding fog has room to drift visibly.
const SKULL_RADIUS = 3.0;

// Wind direction: 60° clockwise from "up" = 30° counter-clockwise
// from +X (card-local right). Upper-right direction.
const FLOW_ANGLE_RAD = Math.PI / 6;
const FLOW_X = Math.cos(FLOW_ANGLE_RAD);
const FLOW_Y = Math.sin(FLOW_ANGLE_RAD);

const VERT_SHADER = /* glsl */ `
  uniform float uLayerT;

  varying vec2  vLocalXY;
  varying float vHeight;

  void main() {
    vLocalXY = position.xy;
    vHeight = uLayerT;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG_SHADER = /* glsl */ `
  precision highp float;

  uniform float uTime;
  uniform vec2  uCardSize;
  uniform float uLayerDensity;
  uniform float uSkullRadius;
  uniform vec2  uFlowDir;
  uniform vec3  uColorCore;
  uniform vec3  uColorTint;
  uniform vec3  uPurpleCore;
  uniform vec3  uPurpleTint;

  varying vec2  vLocalXY;
  varying float vHeight;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i),                hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }
  float fbm(vec2 p) {
    float sum = 0.0;
    float amp = 0.5;
    for (int i = 0; i < 4; i++) {
      sum += amp * vnoise(p);
      p = p * 2.05 + vec2(1.7, 9.2);
      amp *= 0.55;
    }
    return sum;
  }

  // ── SDF primitives ──
  float sdfCircle(vec2 p, vec2 c, float r) {
    return length(p - c) - r;
  }
  float sdfEllipse(vec2 p, vec2 c, vec2 r) {
    return length((p - c) / r) - 1.0;
  }
  float opSmoothUnion(float d1, float d2, float k) {
    float h = clamp(0.5 + 0.5 * (d2 - d1) / k, 0.0, 1.0);
    return mix(d2, d1, h) - k * h * (1.0 - h);
  }

  // Procedural skull-bone SDF.
  //   < 0 inside the skull bone area (where fog is blocked)
  //   > 0 outside the bone (cranium-and-jaw silhouette is broken by
  //         eye sockets and the nasal cavity. Those count as
  //         OUTSIDE the bone, so fog fills them)
  float sdSkullBone(vec2 p) {
    float s = uSkullRadius;
    // Cranium (large circle, slightly above the SDF centre).
    float cranium = sdfCircle(p, vec2(0.0, s * 0.30), s);
    // Jaw (smaller ellipse below). Smooth-union for a face/jaw
    // transition instead of a hard ridge.
    float jaw = sdfEllipse(p, vec2(0.0, -s * 0.65), vec2(s * 0.62, s * 0.45));
    float bone = opSmoothUnion(cranium, jaw, s * 0.22);

    // Eye sockets. SUBTRACT. These regions are NOT skull bone, so
    // fog re-enters there, reading as the iconic two-eye-socket
    // dark pattern inside the cranium silhouette.
    float leftEye  = sdfEllipse(p, vec2(-s * 0.34, s * 0.40), vec2(s * 0.22, s * 0.18));
    float rightEye = sdfEllipse(p, vec2( s * 0.34, s * 0.40), vec2(s * 0.22, s * 0.18));
    float eyes = min(leftEye, rightEye);
    bone = max(bone, -eyes);

    // Nasal cavity. Subtract a small narrow ellipse just below eyes.
    float nose = sdfEllipse(p, vec2(0.0, -s * 0.05), vec2(s * 0.09, s * 0.20));
    bone = max(bone, -nose);

    return bone;
  }

  void main() {
    vec2 p = vLocalXY;

    // Skull bone mask: 1 deep inside the bone region, 0 outside,
    // smooth ~0.3-unit transition for an organic edge.
    float boneSdf = sdSkullBone(p);
    float skullMask = smoothstep(0.3, -0.3, boneSdf);

    // Amorphous radial fog patch centred on the skull. A low-freq
    // noise warps the effective radius by ±1.5 units so the patch
    // boundary is organic and ever-shifting rather than a clean
    // circle. Fog "lobes" wax and wane as the warp drifts.
    float boundaryNoise = vnoise(p * 0.15 + uFlowDir * uTime * 0.08);
    float warpedRadial = max(0.0, length(p) + (boundaryNoise - 0.5) * 3.0);

    // k = 0.06 → visible patch radius ~6 units. Fits cleanly within
    // the card silhouette so the fog stays on the card, doesn't
    // extend past the edges, and the Gaussian fades to near-zero
    // well before reaching the plane boundary (so no visible
    // rectangular edge).
    float patchMask = exp(-warpedRadial * warpedRadial * 0.06);

    // ★ The whole effect in one line: fog where the patch is dense
    // AND not blocked by the skull. The patch defines the overall
    // shape; the skull cuts a face-shaped void out of it.
    float fogMask = patchMask * (1.0 - skullMask);
    if (fogMask < 0.005) discard;

    // Animated FBM noise. Scrolls AGAINST the wind direction so
    // the visible noise features drift WITH the wind. The skull
    // doesn't drift. Only the noise does. The contrast between
    // moving fog and stationary skull-shaped void is what reads
    // as "wind drifting past an obstacle".
    vec2 noiseUv = p * 0.45
                 - uFlowDir * uTime * 0.55
                 + vec2(vHeight * 11.7, vHeight * 4.3);

    // Domain-warped FBM for organic curls.
    vec2 warp = vec2(
      fbm(noiseUv + vec2(0.0, 0.0)),
      fbm(noiseUv + vec2(5.2, 1.3))
    );
    float n = fbm(noiseUv + warp * 1.6);

    // Noise modulates fog density. High floor (0.75) so the fog is
    // always thick. Variation just adds turbulent surface character,
    // it doesn't thin the fog enough to let the skull blur out.
    float noiseMod = 0.75 + 0.25 * smoothstep(0.22, 0.78, n);

    // Slow irregular intensity envelope. Composed sines for an
    // aperiodic feel. Floored at 0.75 so even the "low" phase still
    // reads as dense fog. The skull is always clearly visible, and
    // intensity just controls how OPAQUE the fog is on top of that.
    float intensityPhase = uTime * 0.30 + sin(uTime * 0.13) * 1.6;
    float intensityRaw = 0.5 + 0.5 * sin(intensityPhase);
    float intensity = 0.75 + 0.25 * intensityRaw;

    float density = fogMask * noiseMod * intensity * uLayerDensity;
    if (density < 0.004) discard;

    // ── Chromatic variation: black + purple fog patches.
    //
    // A SECONDARY low-frequency noise (independent from the density
    // noise) decides whether this pixel is in a "black-tone" or
    // "purple-tone" patch. Sampled at p * 0.12 instead of the
    // density noise's 0.45, so colour regions are larger than
    // density features. You see broad purple patches drifting
    // through the fog rather than purple/black per-wisp speckling.
    // Drift offset is slower than the density advection too, so the
    // colour regions don't perfectly track the visible smoke
    // features. Gives the fog ambient-lighting-variation realism.
    float colorN = vnoise(p * 0.12 + uFlowDir * uTime * 0.08
                        + vec2(vHeight * 7.3, vHeight * 13.1));
    float colorBlend = smoothstep(0.32, 0.68, colorN);

    vec3 blackTone  = mix(uColorCore, uColorTint, n);
    vec3 purpleTone = mix(uPurpleCore, uPurpleTint, n);
    vec3 col = mix(blackTone, purpleTone, colorBlend);
    col *= mix(0.78, 1.0, density);

    gl_FragColor = vec4(col, density);
  }
`;

export function createLethalSkullEffect() {
  const group = new THREE.Group();
  group.frustumCulled = false;

  const geometry = new THREE.PlaneGeometry(PLANE_W, PLANE_H);
  const materials = [];
  const tSeed = Math.random() * 100;

  for (let i = 0; i < LAYER_COUNT; i++) {
    const layerT = i / Math.max(LAYER_COUNT - 1, 1);
    const layerHeight = layerT * RISE_HEIGHT;
    // Heavy base. With 4 layers composited via NormalBlending, peak
    // alpha needs to approach opaque so the skull reads clearly even
    // through the noise/intensity modulators. exp(-0.7) is a gentler
    // vertical falloff than before so all four layers contribute
    // meaningfully instead of the top layers fading out.
    const layerDensity = Math.exp(-0.7 * layerT) * 0.85;

    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: tSeed },
        uCardSize: { value: new THREE.Vector2(CARD_WIDTH, CARD_HEIGHT) },
        uLayerT: { value: layerT },
        uLayerDensity: { value: layerDensity },
        uSkullRadius: { value: SKULL_RADIUS },
        uFlowDir: { value: new THREE.Vector2(FLOW_X, FLOW_Y) },
        // Black palette. Near-pure black core with very dark tint
        // at noise peaks. Used in "black patches" of the fog.
        uColorCore: { value: new THREE.Color(0x040408) },
        uColorTint: { value: new THREE.Color(0x141018) },
        // Purple palette. Used in "purple patches" of the fog. The
        // colour-noise blends between this palette and the black
        // palette so the fog has organic chromatic variation. Core
        // is a dark dusk purple; tint is a noticeably saturated dark
        // purple that reads at noise peaks. Both stay dark enough
        // that the overall feel remains menacing rather than mystical.
        uPurpleCore: { value: new THREE.Color(0x0e0418) },
        uPurpleTint: { value: new THREE.Color(0x3a1458) },
      },
      vertexShader: VERT_SHADER,
      fragmentShader: FRAG_SHADER,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.NormalBlending,
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.frustumCulled = false;
    mesh.position.z = layerHeight + CARD_THICKNESS / 2 + 0.05;
    mesh.renderOrder = 14 + i;
    group.add(mesh);
    materials.push(material);
  }

  return { points: group, materials, geometry };
}

const _tmpVec3 = new THREE.Vector3();
const _tmpEuler = new THREE.Euler();
export function updateLethalSkullEffect(effect, dt, cardMesh) {
  if (!effect || !cardMesh) return;

  for (const material of effect.materials) {
    material.uniforms.uTime.value += dt;
  }

  cardMesh.getWorldPosition(_tmpVec3);
  effect.points.position.copy(_tmpVec3);

  const intent = cardMesh.userData?.intentEuler;
  const yaw = intent ? intent.z : 0;
  _tmpEuler.set(-Math.PI / 2, 0, yaw, 'XYZ');
  effect.points.rotation.copy(_tmpEuler);
}

export function disposeLethalSkullEffect(effect) {
  if (!effect) return;
  effect.points.parent?.remove(effect.points);
  effect.geometry?.dispose();
  for (const material of effect.materials || []) {
    material.dispose();
  }
}
